/**
 * Bidirectional black-hole teleporters + arena void rifts.
 * Visuals: layered glow “blur”, accretion disk, spiral arms, orbiting debris.
 */

export class Portal {
    /**
     * @param {number} x
     * @param {number} y
     * @param {number} exitX
     * @param {number} exitY
     * @param {{ kind?: 'link'|'arena-exit'|'arena-enter', hue?: number, radius?: number }=} opts
     */
    constructor(x, y, exitX, exitY, opts = {}) {
        this.x = x;
        this.y = y;
        this.exitX = exitX;
        this.exitY = exitY;
        this.kind = opts.kind || 'link'; // link | arena-exit | arena-enter
        this.radius = opts.radius || (this.kind === 'arena-exit' ? 36 : 32);
        this.cooldownA = 0;
        this.cooldownB = 0;
        this.maxCooldown = this.kind === 'arena-exit' ? 90 : 70;
        this.rotationAngle = Math.random() * Math.PI * 2;
        this.diskAngle = Math.random() * Math.PI * 2;
        this.pulsePhase = Math.random() * Math.PI * 2;
        this.hue = opts.hue ?? (this.kind === 'arena-exit' ? 280 : this.kind === 'arena-enter' ? 300 : 275);
        this.debris = this._seedDebris(10);
        this.sparkT = 0;
        // One-way: only A→exit (used for arena return hole)
        this.oneWay = opts.oneWay ?? this.kind === 'arena-exit';
        // Chance this pair pulls into void arena (only for normal links)
        this.voidChance = opts.voidChance ?? (this.kind === 'link' ? 0.32 : 0);
        this.label = opts.label || null;
    }

    _seedDebris(n) {
        const list = [];
        for (let i = 0; i < n; i++) {
            list.push({
                a: Math.random() * Math.PI * 2,
                r: 0.45 + Math.random() * 0.7,
                s: 0.015 + Math.random() * 0.04,
                size: 1 + Math.random() * 2.2,
                bright: 0.4 + Math.random() * 0.6,
            });
        }
        return list;
    }

    update() {
        this.rotationAngle += 0.055;
        this.diskAngle -= 0.028;
        this.pulsePhase += 0.07;
        this.sparkT += 0.12;
        for (const d of this.debris) {
            d.a += d.s * (this.kind === 'arena-exit' ? 1.4 : 1);
            // Slow spiral inward then reset
            d.r -= 0.0015;
            if (d.r < 0.25) d.r = 0.95 + Math.random() * 0.2;
        }
        if (this.cooldownA > 0) this.cooldownA--;
        if (this.cooldownB > 0) this.cooldownB--;
    }

    /**
     * @returns {{ x: number, y: number, side: 'a'|'b', voidPull?: boolean }|null}
     */
    checkPlayerCollision(player) {
        const px = player.x, py = player.y, ps = player.size / 2;

        // Entry A → exit
        if (this.cooldownA === 0) {
            const dx = this.x - px, dy = this.y - py;
            if (Math.hypot(dx, dy) < this.radius + ps) {
                this.cooldownA = this.maxCooldown;
                this.cooldownB = this.maxCooldown;
                const voidPull = this.kind === 'link' && Math.random() < this.voidChance;
                return {
                    x: this.exitX,
                    y: this.exitY,
                    side: 'a',
                    voidPull: voidPull || this.kind === 'arena-enter',
                    arenaExit: this.kind === 'arena-exit',
                    portal: this,
                };
            }
        }

        // Exit B → entry (bidirectional links only)
        if (!this.oneWay && this.cooldownB === 0) {
            const dx = this.exitX - px, dy = this.exitY - py;
            if (Math.hypot(dx, dy) < this.radius + ps) {
                this.cooldownB = this.maxCooldown;
                this.cooldownA = this.maxCooldown;
                const voidPull = this.kind === 'link' && Math.random() < this.voidChance;
                return {
                    x: this.x,
                    y: this.y,
                    side: 'b',
                    voidPull,
                    arenaExit: false,
                    portal: this,
                };
            }
        }

        return null;
    }

    draw(ctx) {
        ctx.save();
        this.drawPortalHole(ctx, this.x, this.y, this.cooldownA > 0, this.kind === 'arena-exit' ? 'EXIT' : null);
        if (!this.oneWay) {
            this.drawPortalHole(ctx, this.exitX, this.exitY, this.cooldownB > 0, null);
            this._drawLink(ctx);
        } else if (this.label) {
            this._drawLabel(ctx, this.x, this.y - this.radius - 18, this.label);
        }
        ctx.restore();
    }

    _drawLink(ctx) {
        const t = this.sparkT;
        ctx.save();
        // Soft ribbon
        ctx.globalAlpha = 0.12;
        ctx.strokeStyle = `hsla(${this.hue}, 90%, 60%, 1)`;
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.moveTo(this.x, this.y);
        ctx.lineTo(this.exitX, this.exitY);
        ctx.stroke();

        ctx.globalAlpha = 0.35;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 10]);
        ctx.lineDashOffset = -t * 8;
        ctx.beginPath();
        ctx.moveTo(this.x, this.y);
        ctx.lineTo(this.exitX, this.exitY);
        ctx.stroke();
        ctx.setLineDash([]);

        // Traveling sparks along link
        for (let i = 0; i < 3; i++) {
            const u = ((t * 0.08 + i / 3) % 1);
            const sx = this.x + (this.exitX - this.x) * u;
            const sy = this.y + (this.exitY - this.y) * u;
            const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, 5);
            g.addColorStop(0, `hsla(${this.hue}, 100%, 80%, 0.9)`);
            g.addColorStop(1, 'transparent');
            ctx.globalAlpha = 0.7;
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(sx, sy, 5, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.restore();
    }

    _drawLabel(ctx, x, y, text) {
        ctx.save();
        ctx.font = 'bold 11px Orbitron, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const tw = ctx.measureText(text).width;
        ctx.fillStyle = 'rgba(10, 4, 20, 0.75)';
        ctx.strokeStyle = `hsla(${this.hue}, 80%, 65%, 0.7)`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(x - tw / 2 - 8, y - 9, tw + 16, 18, 8);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = `hsla(${this.hue}, 90%, 78%, 1)`;
        ctx.shadowBlur = 8;
        ctx.shadowColor = `hsla(${this.hue}, 100%, 50%, 0.8)`;
        ctx.fillText(text, x, y);
        ctx.restore();
    }

    /**
     * Black hole with soft multi-layer glow (blur stand-in), accretion disk, spirals.
     */
    drawPortalHole(ctx, x, y, onCooldown = false, label = null) {
        const pulse = Math.sin(this.pulsePhase) * 4;
        const R = this.radius + pulse;
        const hue = this.hue;
        const cool = onCooldown ? 0.45 : 1;

        ctx.save();
        ctx.translate(x, y);
        ctx.globalAlpha = cool;

        // ── Soft outer haze (stacked gradients ≈ blur) ──
        for (let i = 4; i >= 1; i--) {
            const rr = R * (1.35 + i * 0.45);
            const g = ctx.createRadialGradient(0, 0, R * 0.2, 0, 0, rr);
            g.addColorStop(0, `hsla(${hue}, 90%, 45%, ${0.08 * cool})`);
            g.addColorStop(0.45, `hsla(${hue}, 85%, 35%, ${0.12 * cool / i})`);
            g.addColorStop(1, 'transparent');
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(0, 0, rr, 0, Math.PI * 2);
            ctx.fill();
        }

        // Accretion disk (tilted ellipse)
        ctx.save();
        ctx.rotate(this.diskAngle * 0.15);
        ctx.scale(1, 0.42);
        const disk = ctx.createRadialGradient(0, 0, R * 0.35, 0, 0, R * 1.55);
        disk.addColorStop(0, 'transparent');
        disk.addColorStop(0.35, `hsla(${hue}, 100%, 70%, ${0.15 * cool})`);
        disk.addColorStop(0.55, `hsla(${hue + 20}, 100%, 60%, ${0.55 * cool})`);
        disk.addColorStop(0.75, `hsla(${hue - 15}, 90%, 40%, ${0.25 * cool})`);
        disk.addColorStop(1, 'transparent');
        ctx.fillStyle = disk;
        ctx.beginPath();
        ctx.arc(0, 0, R * 1.55, 0, Math.PI * 2);
        ctx.fill();
        // Disk rim highlight
        ctx.strokeStyle = `hsla(${hue}, 100%, 75%, ${0.35 * cool})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(0, 0, R * 1.15, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();

        // Spiral arms
        ctx.save();
        ctx.rotate(this.rotationAngle);
        ctx.lineCap = 'round';
        for (let arm = 0; arm < 4; arm++) {
            const offset = (arm / 4) * Math.PI * 2;
            ctx.strokeStyle = `hsla(${hue + arm * 8}, 100%, ${60 + arm * 5}%, ${0.55 * cool})`;
            ctx.lineWidth = 2.2 - arm * 0.25;
            ctx.shadowBlur = 12;
            ctx.shadowColor = `hsla(${hue}, 100%, 50%, 0.8)`;
            ctx.beginPath();
            for (let a = 0; a < Math.PI * 2.2; a += 0.08) {
                const fall = 1 - a / (Math.PI * 2.2);
                const rr = R * 0.95 * fall * fall;
                const px = Math.cos(a + offset) * rr;
                const py = Math.sin(a + offset) * rr;
                if (a === 0) ctx.moveTo(px, py);
                else ctx.lineTo(px, py);
            }
            ctx.stroke();
        }
        ctx.restore();

        // Orbiting debris sparks
        for (const d of this.debris) {
            const px = Math.cos(d.a + this.rotationAngle * 0.3) * R * d.r;
            const py = Math.sin(d.a + this.rotationAngle * 0.3) * R * d.r * 0.55;
            const dg = ctx.createRadialGradient(px, py, 0, px, py, d.size * 2.5);
            dg.addColorStop(0, `hsla(${hue}, 100%, 90%, ${d.bright * cool})`);
            dg.addColorStop(0.4, `hsla(${hue}, 100%, 60%, ${0.5 * cool})`);
            dg.addColorStop(1, 'transparent');
            ctx.fillStyle = dg;
            ctx.beginPath();
            ctx.arc(px, py, d.size * 2.5, 0, Math.PI * 2);
            ctx.fill();
        }

        // Photon ring (sharp bright ring around horizon)
        ctx.shadowBlur = 18;
        ctx.shadowColor = `hsla(${hue}, 100%, 70%, 0.9)`;
        ctx.strokeStyle = `hsla(${hue}, 100%, 80%, ${0.85 * cool})`;
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(0, 0, R * 0.52, 0, Math.PI * 2);
        ctx.stroke();

        // Secondary soft ring
        ctx.shadowBlur = 24;
        ctx.strokeStyle = `hsla(${hue}, 90%, 55%, ${0.4 * cool})`;
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.arc(0, 0, R * 0.72, 0, Math.PI * 2);
        ctx.stroke();

        // Event horizon — pure black core with soft edge
        const core = ctx.createRadialGradient(0, 0, 0, 0, 0, R * 0.48);
        core.addColorStop(0, '#000000');
        core.addColorStop(0.55, '#000000');
        core.addColorStop(0.82, `hsla(${hue}, 80%, 8%, 0.9)`);
        core.addColorStop(1, `hsla(${hue}, 90%, 20%, 0)`);
        ctx.shadowBlur = 0;
        ctx.fillStyle = core;
        ctx.beginPath();
        ctx.arc(0, 0, R * 0.48, 0, Math.PI * 2);
        ctx.fill();

        // Inner swirl hint on horizon edge
        ctx.save();
        ctx.rotate(-this.rotationAngle * 1.6);
        ctx.strokeStyle = `hsla(${hue}, 100%, 70%, ${0.35 * cool})`;
        ctx.lineWidth = 1.5;
        for (let i = 0; i < 2; i++) {
            ctx.beginPath();
            for (let a = 0; a < Math.PI; a += 0.12) {
                const rr = R * 0.28 * (1 - a / Math.PI);
                const px = Math.cos(a + i * Math.PI) * rr;
                const py = Math.sin(a + i * Math.PI) * rr;
                if (a === 0) ctx.moveTo(px, py);
                else ctx.lineTo(px, py);
            }
            ctx.stroke();
        }
        ctx.restore();

        // Breathing outer ring
        const breath = 1 + Math.sin(this.pulsePhase * 1.3) * 0.04;
        ctx.strokeStyle = `hsla(${hue}, 100%, 65%, ${0.55 * cool})`;
        ctx.lineWidth = 2;
        ctx.shadowBlur = 16;
        ctx.shadowColor = `hsla(${hue}, 100%, 50%, 0.7)`;
        ctx.beginPath();
        ctx.arc(0, 0, R * breath, 0, Math.PI * 2);
        ctx.stroke();

        // Cooldown veil
        if (onCooldown) {
            ctx.fillStyle = 'rgba(0,0,0,0.35)';
            ctx.beginPath();
            ctx.arc(0, 0, R * 1.1, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.restore();

        if (label) this._drawLabel(ctx, x, y - R - 16, label);
    }

    static createRandomPair(canvas, opts = {}) {
        const margin = 80;
        const w = canvas.width || 4000;
        const h = canvas.height || 4000;
        const x1 = margin + Math.random() * (w - margin * 2);
        const y1 = margin + Math.random() * (h - margin * 2);

        let x2, y2, tries = 0;
        do {
            x2 = margin + Math.random() * (w - margin * 2);
            y2 = margin + Math.random() * (h - margin * 2);
            tries++;
        } while (Math.hypot(x2 - x1, y2 - y1) < 280 && tries < 40);

        return new Portal(x1, y1, x2, y2, opts);
    }

    /** Single exit black hole for void arena */
    static createArenaExit(x, y) {
        return new Portal(x, y, x, y, {
            kind: 'arena-exit',
            oneWay: true,
            hue: 285,
            radius: 38,
            label: 'ESCAPE RIFT',
            voidChance: 0,
        });
    }
}
